/**
 * Plugin discovery and wiring.
 *
 * Discovers plugin directories under the `plugins/` folders, parses their
 * manifests (see {@link parsePluginDir}), and turns each into a synthetic
 * {@link ExtensionFactory} that registers the plugin's capabilities through the
 * existing ExtensionAPI. The factory is loaded by the standard extension loader,
 * so plugins are just extensions assembled from a manifest instead of code.
 *
 * Capability wiring (minimum):
 *  - skills / themes         → `resources_discover` skill/theme paths
 *  - commands                → `resources_discover` slash-command paths (`.agents/commands`)
 *  - agents                  → `resources_discover` agent paths (`.agents/agents` subagents)
 *  - providers (native only) → `registerProvider`
 *  - hooks                   → shell-protocol bridge (see hooks-bridge.ts)
 *  - mcpServers              → parsed; wiring deferred (see design doc)
 */

import * as fs from "node:fs";
import * as path from "node:path";
import { type ExtensionMcpServerConfig, registerExtensionMcpServers } from "../../extension-mcp-servers.js";
import type { ExtensionAPI, ExtensionFactory } from "../types.js";
import { installPluginHooks } from "./hooks-bridge.js";
import { ensurePluginDataDir, pluginDataDir } from "./locations.js";
import { type NormalizedPlugin, parsePluginDir } from "./manifest.js";

/** Extension path a plugin is loaded under: `<plugin:my-tool>`. */
export function pluginExtensionPath(id: string): string {
	return `<plugin:${id}>`;
}

/** Recover the plugin id from {@link pluginExtensionPath}, or undefined for a non-plugin extension. */
export function pluginIdFromExtensionPath(extensionPath: string): string | undefined {
	const match = /^<plugin:(.+)>$/.exec(extensionPath);
	return match?.[1];
}

/**
 * The template variables a plugin may use, mapped to their values.
 *
 * All vendor spellings are honored rather than only the ones hoocode invented.
 * A plugin does not know which agent is loading it: a Copilot plugin writes
 * `${PLUGIN_ROOT}`, a Claude one `${CLAUDE_PLUGIN_ROOT}`, and either may use
 * `${*_PLUGIN_DATA}` for runtime state. Supporting one spelling means the others
 * reach the shell or the MCP client as literal text.
 */
export function pluginVariables(root: string, dataDir: string): Record<string, string> {
	return {
		PLUGIN_ROOT: root,
		CLAUDE_PLUGIN_ROOT: root,
		AGENTS_PLUGIN_ROOT: root,
		COPILOT_PLUGIN_ROOT: root,
		CLAUDE_PLUGIN_DATA: dataDir,
		COPILOT_PLUGIN_DATA: dataDir,
		AGENTS_PLUGIN_DATA: dataDir,
	};
}

/** Expand every `${VAR}` in {@link pluginVariables}. Unknown `${...}` is left alone. */
function substituteVars(value: string, vars: Record<string, string>): string {
	return value.replace(/\$\{([A-Z_]+)\}/g, (whole, name: string) => vars[name] ?? whole);
}

/** Coerce parsed mcpServers into the standard config shape, substituting root vars. */
function resolveMcpServers(
	mcpServers: Record<string, unknown>,
	vars: Record<string, string>,
): Record<string, ExtensionMcpServerConfig> {
	const out: Record<string, ExtensionMcpServerConfig> = {};
	for (const [name, raw] of Object.entries(mcpServers)) {
		if (!raw || typeof raw !== "object") continue;
		const cfg = raw as ExtensionMcpServerConfig;
		// Remote servers ({ type: "http" | "sse", url }): pass through for the
		// MCP loader's HTTP transports.
		if (typeof cfg.url === "string" && typeof cfg.command !== "string") {
			out[name] = {
				type: cfg.type === "sse" ? "sse" : "http",
				url: cfg.url,
				headers: cfg.headers
					? Object.fromEntries(Object.entries(cfg.headers).map(([k, v]) => [k, substituteVars(String(v), vars)]))
					: undefined,
				background: cfg.background,
			};
			continue;
		}
		if (typeof cfg.command !== "string") continue;
		out[name] = {
			command: substituteVars(cfg.command, vars),
			args: cfg.args?.map((a) => substituteVars(String(a), vars)),
			env: cfg.env
				? Object.fromEntries(Object.entries(cfg.env).map(([k, v]) => [k, substituteVars(String(v), vars)]))
				: undefined,
			background: cfg.background,
		};
	}
	return out;
}

export type { NormalizedPlugin } from "./manifest.js";
export { parsePluginDir } from "./manifest.js";

/**
 * Discover plugins across the given `plugins/` directories.
 * First-wins on duplicate ids (project dirs should be listed before global).
 */
/**
 * A skills directory holds plain skills and plugins side by side, and the
 * manifest is the only thing separating them. Everywhere else the manifest is
 * optional and components in their default locations are enough.
 */
function isSkillsDirectory(dir: string): boolean {
	return path.basename(dir) === "skills";
}

export function discoverPlugins(pluginDirs: string[]): NormalizedPlugin[] {
	const plugins: NormalizedPlugin[] = [];
	const seen = new Set<string>();

	for (const dir of pluginDirs) {
		if (!fs.existsSync(dir)) continue;
		const requireManifest = isSkillsDirectory(dir);
		let entries: fs.Dirent[];
		try {
			entries = fs.readdirSync(dir, { withFileTypes: true });
		} catch {
			continue;
		}
		for (const entry of entries) {
			if (!entry.isDirectory() && !entry.isSymbolicLink()) continue;
			const plugin = parsePluginDir(path.join(dir, entry.name), { requireManifest });
			if (plugin && !seen.has(plugin.id)) {
				seen.add(plugin.id);
				plugins.push(plugin);
			}
		}
	}

	return plugins;
}

export interface PluginFactoryOptions {
	/**
	 * Load only capabilities that cannot execute: skills, commands, subagents,
	 * themes. Hooks and MCP servers are skipped and reported.
	 *
	 * Set for **project-scoped** plugins — those discovered under the workspace,
	 * chiefly `<cwd>/.claude/skills/`. That content arrives with a cloned
	 * repository rather than from the user, and registering shell hooks or
	 * spawning MCP servers from it with no confirmation is a real escalation over
	 * reading skill text, which is all a project skills directory gets today.
	 *
	 * Claude Code gates the same content behind a workspace trust dialog and
	 * per-server MCP approval. hoocode has no trust mechanism at all, so it
	 * withholds the executable half instead of pretending to gate it. If a trust
	 * gate is ever added, this is the flag it replaces.
	 * See docs/plugin-system-architecture.md §5.9.
	 */
	passiveOnly?: boolean;
}

/** Capabilities withheld from a passive-only plugin, for reporting. */
export function withheldCapabilities(plugin: NormalizedPlugin): string[] {
	return [plugin.hooks && "hooks", plugin.mcpServers && "mcp servers"].filter((x): x is string => !!x);
}

/** Build a synthetic extension factory that wires one normalized plugin. */
export function buildPluginFactory(plugin: NormalizedPlugin, options?: PluginFactoryOptions): ExtensionFactory {
	const passiveOnly = options?.passiveOnly === true;
	// Only the executable capabilities can reference the data dir, so it is
	// created only when one of them is actually wired — no empty directory per
	// installed plugin.
	const wiresExecutables = !passiveOnly && !!(plugin.hooks || plugin.mcpServers);
	const vars = pluginVariables(
		plugin.root,
		wiresExecutables ? ensurePluginDataDir(plugin.id) : pluginDataDir(plugin.id),
	);
	const factory: ExtensionFactory = (hoo: ExtensionAPI) => {
		// Resources: contribute the plugin's capability directories. Commands map to
		// the slash-command surface (`.agents/commands`) and agents to subagent
		// definitions (`.agents/agents`), matching hoocode's native conventions.
		if (plugin.skillsDir || plugin.commandsDir || plugin.themesDir || plugin.agentsDir) {
			hoo.on("resources_discover", () => ({
				skillPaths: plugin.skillsDir ? [plugin.skillsDir] : undefined,
				themePaths: plugin.themesDir ? [plugin.themesDir] : undefined,
				slashCommandPaths: plugin.commandsDir ? [plugin.commandsDir] : undefined,
				agentPaths: plugin.agentsDir ? [plugin.agentsDir] : undefined,
			}));
		}

		// Providers (native plugins only).
		for (const provider of plugin.providers ?? []) {
			hoo.registerProvider(provider.name, provider.config);
		}

		// Hooks: true-parity shell bridge. Executable, so withheld from a
		// project-scoped plugin (see PluginFactoryOptions.passiveOnly).
		if (plugin.hooks && !passiveOnly) {
			installPluginHooks(hoo, plugin.hooks, plugin.root, vars, () => {
				// Non-blocking hook failures are intentionally quiet (Claude Code parity).
			});
		}

		// MCP servers: register for the hoo-core mcp-loader to connect on session_start.
		// Executable, so withheld from a project-scoped plugin.
		if (plugin.mcpServers && !passiveOnly) {
			registerExtensionMcpServers(plugin.id, resolveMcpServers(plugin.mcpServers, vars));
		}
	};

	factory.displayName = `plugin:${plugin.id}`;
	return factory;
}
